import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { MdOutlineDelete } from "react-icons/md";
import { IoReceiptOutline } from "react-icons/io5";
import { FaRegEdit } from "react-icons/fa";
import { motion } from "framer-motion";
import { IoCashOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { FaRegFolderClosed } from "react-icons/fa6";
import axios from "axios";
import { useCustomDialog } from "../../context/DialogProvider";
import { toast } from "react-toastify";
import { IoLocationOutline } from "react-icons/io5";
import EmployeeListLoading from "../../utils/EmployeeListLoading";
import { MdAlternateEmail } from "react-icons/md";
import { useUser } from "../../context/UserContext";
import { TbCalendarStats } from "react-icons/tb";  // Add this import
import { HiOutlineUpload } from "react-icons/hi";
import api from "../../utils/api";

// Add this utility function at the top
const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const employeesPerPage = 8;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { alert, confirm } = useCustomDialog();
  const fileInputRef = useRef(null);
  const [totalPages, setTotalPages] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const { currentUser } = useUser();
  const baseRoute = currentUser?.role === "HR" ? "/employee" : "/admin";

  const [input, setInput] = useState(0);

  // Fetch Employees function
  const fetchEmployees = async (page = 1) => {
    setIsLoading(true);
    try {
      const searchTerm = searchParams.get("search") || "";
      const response = await api.get(
        `/admin/homepage?page=${page - 1}&size=${employeesPerPage}&search=${searchTerm}`
      );
      const data = response.data;
      setEmployees(data.users);
      setTotalPages(data.totalPages);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch employees on page change
  useEffect(() => {
    fetchEmployees(currentPage);
  }, [currentPage]);

  // Fetch employees on search param change
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search changes
    fetchEmployees(1);
  }, [searchParams]);

  // Delete employee handler
  const handleDeleteClick = (emp) => {
    setEmployeeToDelete(emp);
    setShowDeleteModal(true);
  };

  const navigate = useNavigate();

  // Throttled handlers
  const throttledDeleteClick = throttle(handleDeleteClick, 300);
  const throttledNavigate = throttle((path) => navigate(path), 300);

  // File change handler
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Upload function
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.warning("Please choose a file first.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post(`/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Upload successful ✅", {
        position: "bottom-right",
        autoClose: 3000,
      });
      fileInputRef.current.value = "";
      setFile(null);
      fetchEmployees();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed ❌");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete employee function
  const deleteEmployee = async (id) => {
  const confirmed = await confirm("Are you sure you want to delete this employee?");
  if (!confirmed) return;

  try {
    const response = await api.delete(`/deleteEmp/${id}`);
    
    toast.success(response.data, {
      position: "bottom-right",
      autoClose: 3000,
    });

    fetchEmployees(currentPage); // Refresh employee list
  } catch (error) {
    console.error("Error deleting user:", error);
    toast.error(
      error.response?.data || "Error occurred while deleting the user.",
      { position: "bottom-right", autoClose: 3000 }
    );
  }
};



  const goToPage = () => {
    if (input > 0 && input <= totalPages) {
      setCurrentPage(input);
      setInput(0); // Reset input after navigating
    } else {
      alert("Please enter a valid page number.");
    }
  }

  // Update all navigation paths to use the dynamic baseRoute
  const handleNavigation = (path) => {
    throttledNavigate(`${baseRoute}${path}`);
  };

  return (
    <div className=" overflow-hidden p-2 xs:p-3 sm:p-4">
      {/* Header section */}
      <motion.div className="flex flex-col sm:flex-row justify-between sm:gap-2 gap-4 mt-1 mb-4">
        <div className="min-w-0">
          <h2 className="text-xl xs:text-2xl font-bold truncate">Employees</h2>
        </div>
        <div className="flex flex-col xs:flex-row gap-3 xs:gap-5">
          <div className="w-full xs:w-auto">
            <form className="flex items-center gap-2 " onSubmit={handleUpload}>
              <label
                htmlFor="myfile"
                className="flex  items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-full cursor-pointer text-sm whitespace-nowrap"
              >
                <FaRegFolderClosed className="shrink-0" />
                <span className="truncate">{file ? file.name : "Choose"}</span>
              </label>
              <input
                type="file"
                id="myfile"
                name="file"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
                accept=".xlsx, .xls"
              />
              <button
                type="submit"
                disabled={isUploading}
                className={`bg-slate-800 text-white px-6 py-2 rounded-full transition-all text-sm whitespace-nowrap flex items-center gap-2
                           ${
                             isUploading
                               ? "opacity-75 cursor-not-allowed"
                               : "hover:bg-slate-700"
                           }`}
              >
                
                {isUploading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  [<HiOutlineUpload className="text-xl"/>,
                  "Upload"]
                )}
              </button>
            </form>
          </div>
          <div className="w-full xs:w-auto">
            {/* <button
              onClick={() => throttledNavigate("/admin/employees/AddEmployee")}
              className="w-full xs:w-auto bg-slate-700 text-white px-6 py-2 rounded-full hover:bg-slate-700 transition-all text-sm  whitespace-nowrap"
            >
              Add
            </button> */}
          </div>
        </div>
      </motion.div>

      {/* Table Section - Only visible on larger screens */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        transition={{ duration: 0.2 }}
      >
        <div className="overflow-x-auto">
          {isLoading ? (
    <EmployeeListLoading/>
  ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  S.No
                </th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  Name
                </th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  Location
                </th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  Email
                </th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  Emplyee ID
                </th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees && employees.length > 0 ? (
                employees.map((emp, index) => (
                  <tr
                    key={emp.id || index}
                    className="hover:bg-slate-50/50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 text-slate-600">
                      {((currentPage - 1) * employeesPerPage) + index + 1}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-700">
                      {emp.name}
                    </td>
                    <td className="px-6 py-4 text-slate-600">{emp.location}</td>
                    <td className="px-6 py-4 text-slate-600">{emp.email}</td>
                    <td className="px-6 py-4 text-slate-600">
                      {emp.employeeId}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-3">
                        <motion.button
                          onClick={() => handleNavigation(`/employees/leavebalance/${emp.id}`)}
                          className="text-purple-600 hover:text-purple-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Leave Balance"
                        >
                          <TbCalendarStats className="text-xl" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleNavigation(`/employees/salary/${emp.id}`)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Salary Details"
                        >
                          <IoCashOutline className="text-xl" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleNavigation(`/employees/payslips/${emp.id}`)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="View Payslips"
                        >
                          <IoReceiptOutline className="text-xl" />
                        </motion.button>
                        <motion.button
                        onClick={() => handleNavigation(`/employees/EditEmployee/${emp.id}`)}
                          className="text-yellow-600 hover:text-yellow-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Edit Employee"
                        >
                          <FaRegEdit className="text-xl" />
                        </motion.button>
                        <motion.button
                          onClick={() => deleteEmployee(emp.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Delete Employee"
                        >
                          <MdOutlineDelete className="text-xl" />
                        </motion.button>
                        
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-slate-400"
                  >
                    No employees found
                  </td>
                </tr>
              )}
            </tbody>
          </table>)}
        </div>

        {/* Pagination Section */}
        <div className="px-6 py-4 border-t border-slate-100 flex justify-between items-center">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
             count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#ffffff",
                },
              }}
            />
          </Stack>

          <div className="flex gap-2 items-center ">
            <input
             type="number"
             value={input}  // controlled by React state
             onChange={(e) => setInput(e.target.value)}  // update state on change
             className="border border-gray-300 rounded-md w-12 p-2 text-center"
             />

            <button 
            className="bg-slate-700 text-white px-4 py-2 rounded-md"
             onClick={goToPage}>
            Go
            </button>
          
        </div>
        </div>

       
      </motion.div>

      {/* Mobile Cards View */}
      <motion.div
        className="sm:hidden mt-4 pb-14"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="grid grid-cols-1 gap-4">
          {employees.length > 0 ? (
            employees.map((emp, index) => (
              <motion.div
                key={index}
                className="bg-white p-5 rounded-xl shadow-md border border-slate-100"
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 text-lg">
                      {emp.name}
                    </h3>
                    <span className="text-sm font-medium text-slate-500">
                      {emp.employeeId}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-base text-slate-600 flex items-center gap-3">
                      <span className="w-5 h-5 text-slate-400"><IoLocationOutline /></span>
                      {emp.location}
                    </p>
                    <p className="text-base text-slate-600 flex items-center gap-3">
                      <span className="w-5 h-5 text-slate-400"><MdAlternateEmail /></span>
                      {emp.email}
                    </p>
                  </div>
                  <div className="pt-4 mt-4 border-t border-slate-100">
                    <div className="flex justify-around items-center">
                      <motion.button
                        onClick={() => handleNavigation(`/employees/leavebalance/${emp.id}`)}
                        className="p-3 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <TbCalendarStats className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleNavigation(`/employees/salary/${emp.id}`)}
                        className="p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IoCashOutline className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleNavigation(`/employees/payslips/${emp.id}`)}
                        className="p-3 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IoReceiptOutline className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleNavigation(`/employees/EditEmployee/${emp.id}`)}
                        className="p-3 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaRegEdit className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => throttledDeleteClick(emp)}
                        className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <MdOutlineDelete className="text-xl"/>
                      </motion.button>
                      
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-400 bg-white rounded-xl">
              No employees found
            </div>
          )}
        </div>

        {/* Mobile Pagination */}
        <div className="mt-6 flex justify-center">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
             count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#ffffff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>
    </div>
  );
}

export default Employees;
